import { FormEvent, useState } from 'react';
import { Elements, CardElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { accountBillingApi } from '@/services/endpoints/account/billing';
import { isStripeConfigured, stripePromise } from '@/services/api/stripeClient';
import { ACCOUNT_BILLING_KEYS } from './queryKeys';
import { Notice } from './accountUi';

/**
 * Saves a card with Stripe Elements. The card number never reaches our servers:
 * the backend issues a SetupIntent, Elements confirms it directly with Stripe
 * (including any 3-D Secure challenge), and only the resulting id comes back.
 */

function CardFields({ onDone, onCancel }: { onDone: () => void; onCancel: () => void }) {
  const stripe = useStripe();
  const elements = useElements();
  const queryClient = useQueryClient();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    if (!stripe || !elements) return;
    const card = elements.getElement(CardElement);
    if (!card) return;

    setSubmitting(true);
    setError(null);
    try {
      const clientSecret = await accountBillingApi.createSetupIntent();
      const result = await stripe.confirmCardSetup(clientSecret, {
        payment_method: { card },
      });

      if (result.error) {
        setError(result.error.message ?? 'That card could not be saved.');
        return;
      }

      await queryClient.invalidateQueries({ queryKey: ACCOUNT_BILLING_KEYS.paymentMethods });
      await queryClient.invalidateQueries({ queryKey: ACCOUNT_BILLING_KEYS.dashboard });
      onDone();
    } catch (caught) {
      const message =
        (caught as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        'That card could not be saved. Try again, or use a different card.';
      setError(message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="rounded-lg border border-input bg-background px-3 py-3.5">
        <CardElement
          options={{
            style: {
              base: {
                fontSize: '14px',
                fontFamily: 'Inter, system-ui, sans-serif',
                color: 'inherit',
                '::placeholder': { color: '#94a3b8' },
              },
              invalid: { color: '#ef4444' },
            },
          }}
        />
      </div>

      {error && (
        <Notice tone="bad" title="Card not saved">
          {error}
        </Notice>
      )}

      <div className="flex items-center gap-2">
        <Button type="submit" disabled={!stripe || submitting}>
          {submitting ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
              Saving
            </>
          ) : (
            'Save card'
          )}
        </Button>
        <Button type="button" variant="ghost" onClick={onCancel} disabled={submitting}>
          Cancel
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">
        Card details go straight to Stripe. AI2Aim stores only the last four digits.
      </p>
    </form>
  );
}

export default function AddCardForm({
  onDone,
  onCancel,
}: {
  onDone: () => void;
  onCancel: () => void;
}) {
  if (!isStripeConfigured) {
    return (
      <Notice tone="warn" title="Card entry is not configured">
        This site has no Stripe publishable key set (<code>VITE_STRIPE_PUBLISHABLE_KEY</code>), so
        the card form cannot load. Use the Stripe billing portal below, or set the key to enable
        card entry here.
      </Notice>
    );
  }

  return (
    <Elements stripe={stripePromise}>
      <CardFields onDone={onDone} onCancel={onCancel} />
    </Elements>
  );
}
